import React from 'react';
import { useRouteData } from './useRouteData';
import { refreshUser } from './login';

/**
 * Manages membership refresh attempts per route to prevent duplicate refreshes
 */
export class MembershipRefreshTracker {
  constructor(debounceMs = 5000, maxRoutes = 10) {
    this.attempts = new Map();
    this.debounceMs = debounceMs;
    this.maxRoutes = maxRoutes;
  }

  /**
   * Check if we should attempt to refresh membership data for the given route
   * @param {string} pathname - The route pathname to check
   * @returns {boolean} True if refresh should be attempted
   */
  shouldRefresh(pathname) {
    const lastAttempt = this.attempts.get(pathname);
    if (!lastAttempt) return true;

    const timeSinceLastAttempt = Date.now() - lastAttempt;
    return timeSinceLastAttempt > this.debounceMs;
  }

  /**
   * Mark that a membership refresh attempt was made for the given route
   * @param {string} pathname - The route pathname to mark
   */
  markAttempted(pathname) {
    this.attempts.set(pathname, Date.now());
    this.cleanup();
  }

  /**
   * Clean up old entries to prevent memory leaks
   * Keeps only the most recent routes up to maxRoutes
   */
  cleanup() {
    if (this.size() <= this.maxRoutes) return;

    const entries = Array.from(this.attempts.entries());
    // Sort by timestamp (most recent first)
    entries.sort((a, b) => b[1] - a[1]);

    this.clear();
    entries
      .slice(0, this.maxRoutes)
      .forEach(([path, time]) => {
        this.attempts.set(path, time);
      });
  }

  clear() {
    this.attempts.clear();
  }

  size() {
    return this.attempts.size;
  }
}

/**
 * Global ref to store the refetch function for user memberships
 * This allows the error notifier (which runs outside React context) to access it
 */
let globalRefetchUserMemberships = null;

/**
 * Get the global refetch function for user memberships
 * Used by error notifier outside React context
 */
export const getRefetchUserMemberships = () => globalRefetchUserMemberships;

/**
 * Set the global refetch function for user memberships
 * Called from RouteDataProvider to register the refetch function
 */
export const setRefetchUserMemberships = (refetchFn) => {
  globalRefetchUserMemberships = refetchFn;
};

// Singleton instance for tracking membership refresh attempts
const membershipRefreshTracker = new MembershipRefreshTracker();

/**
 * Attempt to refresh user memberships to update ACL
 * Checks if a refresh should be attempted based on debouncing logic
 * @param {string} pathname - The current route pathname (for tracking refresh attempts)
 */
export const attemptToRefreshUserMemberships = (pathname) => {
  // Check if we should refresh based on debouncing logic
  if (!membershipRefreshTracker.shouldRefresh(pathname)) {
    return;
  }

  // Global refetch function created in useRouteData hook
  const refetchUserMembershipsFn = getRefetchUserMemberships();

  if (refetchUserMembershipsFn) {
    refetchUserMembershipsFn()
      .then((currentUser) => {
        if (currentUser) {
          refreshUser(currentUser);
          console.debug("user access rights refreshed");
        }
      })
      .catch(error => {
        // Don't show error here - the refetch callback handles it
        console.debug("Error refreshing user memberships:", error);
      })
      .finally(() => {
        // Mark refresh attempt to prevent duplicate refreshes on this route
        membershipRefreshTracker.markAttempted(pathname);
      });
  } else {
    // If refetch function is not available (e.g., RouteDataProvider not mounted yet),
    // Don't show error here - the caller will show it
    console.debug("Refetch function not available - RouteDataProvider may not be mounted");
    membershipRefreshTracker.markAttempted(pathname);
  }
};


export const MembershipContext = React.createContext(null);

/**
 * Hook to access membership data from context.
 * Must be used within MembershipProvider.
 */
export const useMembership = () => {
  const context = React.useContext(MembershipContext);
  if (!context) {
    throw new Error('useMembership must be used within MembershipProvider');
  }
  return context;
};

/**
 * Provider that extracts membership and permission data from route data.
 *
 * Provides:
 * - Group or Site memberships (for account routes)
 * - Account role and membership (for account routes)
 * - Account issues filter (populated from GraphQL only - for account routes)
 * - Agency role and membership (for agency routes)
 *
 * @example
 * <RouteDataProvider>
 *   <MembershipProvider>
 *     <YourRoutes />
 *   </MembershipProvider>
 * </RouteDataProvider>
 */
export function MembershipProvider({ children }) {
  const routeData = useRouteData();
  const { currentUser, isAgencyRoute } = routeData;

  const value = React.useMemo(() => {
    const agencyMembership = currentUser?.agencyMembership || null;
    const agencyRole = agencyMembership?.role || null;
    const accountRole = !isAgencyRoute ? currentUser?.currentRole : null;
    const accountMembership = !isAgencyRoute ? currentUser?.currentMembership : null;
    const accountIssuesFilter = accountMembership?.issuesFilter || null;
    const groupMemberships = !isAgencyRoute ? currentUser?.groupMemberships : [];
    const siteMemberships = !isAgencyRoute ? currentUser?.siteMemberships : [];

    return {
      // Agency route data
      agencyRole,
      agencyMembership,
      hasAgencyRole: agencyRole !== null,

      // Account route data
      accountRole,
      accountMembership,
      accountIssuesFilter,
      groupMemberships,
      siteMemberships,
    };
  }, [
    currentUser?.agencyMembership,
    currentUser?.groupMemberships,
    currentUser?.siteMemberships,
    currentUser?.currentRole,
    currentUser?.currentMembership,
    isAgencyRoute,
  ]);

  return (
    <MembershipContext.Provider value={value}>
      {children}
    </MembershipContext.Provider>
  );
}
